/* Presentations — the shapes from deckLayout.js, written as a real .pptx.
 *
 * pptxgenjs is handed IN rather than imported at the top, so the browser only
 * loads it when she presses Download (`downloadDeck` imports it on demand) —
 * roughly half a megabyte nobody else pays for (check 4). The node sample
 * script and the tests pass it in directly.
 *
 * COST: nothing. The file is built in her browser; no /api call.
 */

import { SLIDE_H, SLIDE_W, layoutDeck } from "./deckLayout";

/* ── A placeholder picture, made here ────────────────────────────────────────
   A tiny solid PNG, stretched to fill its slot. Written by hand (stored, not
   compressed, deflate blocks) so this needs neither a canvas nor zlib and runs
   the same in the browser, in node and in jest. 16×9 px is ~500 bytes. */
/* A plain function, not an IIFE: `const NAME = (() =>` is the shape
   src/hookOrder.test.js sweeps for as a possible component. */
function crcTable() {
  const t = new Uint32Array(256);
  for (let n = 0; n < 256; n++) {
    let c = n;
    for (let k = 0; k < 8; k++) c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
    t[n] = c >>> 0;
  }
  return t;
}
const CRC_TABLE = crcTable();

function crc32(bytes) {
  let c = 0xffffffff;
  for (const b of bytes) c = CRC_TABLE[(c ^ b) & 0xff] ^ (c >>> 8);
  return (c ^ 0xffffffff) >>> 0;
}

function adler32(bytes) {
  let a = 1;
  let b = 0;
  for (const x of bytes) { a = (a + x) % 65521; b = (b + a) % 65521; }
  return ((b << 16) | a) >>> 0;
}

const u32 = (n) => [(n >>> 24) & 255, (n >>> 16) & 255, (n >>> 8) & 255, n & 255];

function chunk(type, data) {
  const typed = [...type].map((ch) => ch.charCodeAt(0));
  return [...u32(data.length), ...typed, ...data, ...u32(crc32([...typed, ...data]))];
}

function toBase64(bytes) {
  let bin = "";
  for (const b of bytes) bin += String.fromCharCode(b);
  if (typeof btoa === "function") return btoa(bin);
  return Buffer.from(bytes).toString("base64");   // node without btoa
}

/** A solid-colour PNG as "image/png;base64,…" — the form pptxgenjs takes. */
export function solidPng(hexColour, w = 16, h = 9) {
  const [r, g, bl] = [0, 2, 4].map((i) => parseInt(hexColour.slice(i, i + 2), 16));
  const raw = [];
  for (let y = 0; y < h; y++) {
    raw.push(0);                                  // filter: none
    for (let x = 0; x < w; x++) raw.push(r, g, bl);
  }
  // One stored deflate block — raw is well under 65535 bytes at this size.
  const len = raw.length;
  const deflate = [0x78, 0x01, 0x01, len & 255, (len >>> 8) & 255, ~len & 255, (~len >>> 8) & 255, ...raw, ...u32(adler32(raw))];
  const ihdr = [...u32(w), ...u32(h), 8, 2, 0, 0, 0];   // 8-bit RGB
  const png = [137, 80, 78, 71, 13, 10, 26, 10, ...chunk("IHDR", ihdr), ...chunk("IDAT", deflate), ...chunk("IEND", [])];
  return `image/png;base64,${toBase64(png)}`;
}


/* ── Shapes → PowerPoint ─────────────────────────────────────────────────── */

function addShape(pres, slide, sh, ratios = {}) {
  if (sh.kind === "rect") {
    const fill = sh.transparency ? { color: sh.fill, transparency: sh.transparency } : { color: sh.fill };
    slide.addShape(pres.shapes.RECTANGLE, { x: sh.x, y: sh.y, w: sh.w, h: sh.h, fill, line: { color: sh.fill, width: 0, ...(sh.transparency ? { transparency: 100 } : {}) } });
    return;
  }
  if (sh.kind === "line") {
    slide.addShape(pres.shapes.LINE, { x: sh.x, y: sh.y, w: sh.w, h: 0, line: { color: sh.color, width: sh.weight || 2 } });
    return;
  }
  if (sh.kind === "image") {
    /* CROPPED TO FILL, LIKE THE PREVIEW — never stretched. pptxgenjs crops
       ("cover") only when told the picture's own proportions, which only the
       browser can measure, so `downloadDeck` measures and hands them in. A
       picture it could not measure falls back to filling its box as before. */
    const ratio = sh.src ? ratios[sh.src] : null;
    const base = { x: sh.x, y: sh.y, w: sh.w, h: sh.h, altText: sh.alt || "", ...(sh.rounding ? { rounding: true } : {}) };
    if (ratio && !sh.placeholder) Object.assign(base, { h: sh.w * ratio, sizing: { type: "cover", w: sh.w, h: sh.h } });
    if (sh.placeholder) slide.addImage({ ...base, data: solidPng(sh.fill || "CCCCCC") });
    else if (/^data:/.test(sh.src)) slide.addImage({ ...base, data: sh.src.replace(/^data:/, "") });
    else slide.addImage({ ...base, path: sh.src });
    return;
  }
  if (sh.kind === "text") {
    if (!String(sh.text || "").trim()) return;   // an empty field draws nothing
    slide.addText(sh.text, {
      x: sh.x, y: sh.y, w: sh.w, h: sh.h, margin: 0,
      fontFace: sh.fontFace, fontSize: sh.fontSize, color: sh.color,
      bold: !!sh.bold, italic: !!sh.italic, align: sh.align, valign: sh.valign,
      // Long copy shrinks to its box when PowerPoint opens the file, rather
      // than spilling over the next shape.
      fit: "shrink",
      ...(sh.link ? { hyperlink: { url: sh.link } } : {}),
    });
  }
}

/**
 * Build the presentation object.
 * @param PptxGenJS the pptxgenjs constructor
 * @param deck { title, author, slides: [{ type, content, notes }], skin, design }
 * @returns { pres, laidOut } — laidOut.skin.missing names any role that fell back
 */
export function buildPptx(PptxGenJS, { title = "", author = "", slides = [], skin = {}, design = "editorial", imageRatios = {} } = {}) {
  const pres = new PptxGenJS();
  pres.layout = "LAYOUT_WIDE";   // 13.333 × 7.5 in — the size deckLayout draws on
  pres.title = title;
  pres.author = author;
  const laidOut = layoutDeck(slides, skin, design);
  for (const ls of laidOut.slides) {
    const slide = pres.addSlide();
    slide.background = { color: ls.background };
    for (const sh of ls.shapes) addShape(pres, slide, sh, imageRatios);
    // Her script for this slide — what she presents from.
    if (ls.notes) slide.addNotes(ls.notes);
  }
  return { pres, laidOut };
}

/** A file name from the deck's title. */
export function deckFileName(title) {
  const base = String(title || "").replace(/[\\/:*?"<>|]+/g, "").replace(/\s+/g, " ").trim() || "Presentation";
  return `${base}.pptx`;
}

/** Every picture's height ÷ width, keyed by its link — read by loading each
 *  one in the browser. One that fails to load is left out, and is drawn
 *  filling its box. */
async function measureImages(deck) {
  const laid = layoutDeck(deck?.slides || [], deck?.skin || {}, deck?.design);
  const srcs = [...new Set(laid.slides.flatMap((s) => s.shapes).filter((sh) => sh.kind === "image" && sh.src).map((sh) => sh.src))];
  const pairs = await Promise.all(srcs.map((src) => new Promise((done) => {
    const img = new Image();
    img.onload = () => done(img.naturalWidth ? [src, img.naturalHeight / img.naturalWidth] : null);
    img.onerror = () => done(null);
    img.src = src;
  })));
  return Object.fromEntries(pairs.filter(Boolean));
}

/** In the browser: build the deck and hand her the file. Loads pptxgenjs on
 *  the first press only. */
export async function downloadDeck(deck) {
  const { default: PptxGenJS } = await import("pptxgenjs");
  const { pres } = buildPptx(PptxGenJS, { ...deck, imageRatios: await measureImages(deck) });
  return pres.writeFile({ fileName: deckFileName(deck?.title) });
}

export { SLIDE_W, SLIDE_H };
